import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict'
import { enUS } from 'date-fns/locale/en-US'

import { DATE_MEDIUM_FORMATTER } from '$lib/constants/formatters'
import type { ChainType, GovernanceType } from '$lib/models'
import * as m from '$lib/paraglide/messages'
import type { LaunchedState } from '$lib/tanstack-table/launched-state-feature'

import { toFixedNumber } from '../numberHelpers'

const percentFormatter = new Intl.NumberFormat('en-US', {
	style: 'percent',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
})

export const prettifyChange = (value: number | null | undefined) =>
	percentFormatter.format(value != null ? Math.abs(value) : 0)

export const prettifyTxns = (
	value: number | null | undefined,
	form: 'long' | 'short' | 'none' = 'short',
	notation: 'standard' | 'scientific' | 'engineering' | 'compact' = 'standard'
) => {
	if (value == null) {
		return m.metrics_soon()
	}

	let unit = ` ${m.metrics_txns_short()}`

	if (form === 'long') {
		unit = ` ${m.metrics_txns_full()}`
	} else if (form === 'none') {
		unit = ''
	}

	return `${value.toLocaleString('en-US', { notation })}${unit}`
}

export const prettifyTps = (
	value: number | null | undefined,
	form: 'long' | 'short' | 'none' = 'short',
	notation: 'standard' | 'scientific' | 'engineering' | 'compact' = 'standard'
) => {
	if (value == null) {
		return m.metrics_soon()
	}

	let unit = ` ${m.metrics_tps_short()}`

	if (form === 'long') {
		unit = ` ${m.metrics_tps_full()}`
	} else if (form === 'none') {
		unit = ''
	}

	if (value >= 1000) {
		return `${toFixedNumber(value, 0).toLocaleString('en-US', { notation })}${unit}`
	}

	if (value >= 100) {
		return `${toFixedNumber(value, 1).toLocaleString('en-US', { notation })}${unit}`
	}

	if (value >= 0.01) {
		return `${toFixedNumber(value, 2).toLocaleString('en-US')}${unit}`
	}

	if (value > 0) {
		return `${value.toLocaleString('en-US', { minimumFractionDigits: 4 })}${unit}`
	}

	return `0${unit}`
}

export const prettifyTime = (value: number | null | undefined) => {
	if (value == null) {
		return m.metrics_soon()
	}

	const clearedValue = toFixedNumber(value, 2)

	const hours = Math.floor(clearedValue / 3600)
	const minutes = Math.floor(clearedValue / 60)
	const seconds = Math.floor(clearedValue % 60)

	if (hours > 0) {
		return `${hours}h`
	}

	if (minutes === 0) {
		const doesHaveMs = Number.isInteger(clearedValue)

		return `${toFixedNumber(value, doesHaveMs ? 0 : 2).toLocaleString('en-US')}s`
	}

	if (seconds === 0) {
		return `${minutes}m`
	}

	return `${minutes}m ${seconds}s`
}

export const prettifyType = (type: ChainType | null | undefined, isLong = true) => {
	switch (type) {
		case 'layer_1':
			return isLong ? m.metrics_type_l1_long() : m.metrics_type_l1_short()
		case 'layer_2':
			return isLong ? m.metrics_type_l2_long() : m.metrics_type_l2_short()
		case 'layer_1_parachain':
			return m.metrics_type_parachain()
		default:
			return m.metrics_soon()
	}
}

export const prettifyLaunchDate = (value: Date | null | undefined, state: LaunchedState) => {
	if (value == null) {
		return m.metrics_soon()
	}

	if (state === 'date') {
		return DATE_MEDIUM_FORMATTER.format(value)
	}

	return formatDistanceToNowStrict(value, { locale: enUS, addSuffix: true })
}

export const prettifyGovernance = (value: GovernanceType | null | undefined) => {
	if (value == null) {
		return m.metrics_soon()
	}

	return m[`governance_${value}`]()
}
